import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";

import { motion } from "framer-motion";
import axiosToken from "../../api/axiosClient";
import { toast } from "react-toastify";
import { OTPInput } from "../../components/OTPInput";
import { MdVerified } from "react-icons/md";
import logo200 from "../../assets/logo/logo-200.png";
import InputField from "../../components/form/InputField";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { callback } = useParams(); // récupère callback dynamique depuis l'URL
  const codeLength = 8;
  const [loading, setLoading] = useState(false);
  const [focusOptInput, setFocusOptInput] = useState(true);
  const [authToken, setAuthToken] = useState(null);

  useEffect(() => {
    const email = sessionStorage.getItem("forgot-email");

    formik.setFieldValue("email", email);

    if (!email) {
      console.error("No email in session, redirecting to reset password");
      toast.error("Aucun email trouvé, reprendre la réinitialisation.", {
        position: "bottom-left",
      });

      setTimeout(() => {
        navigate(-1); // redirige vers le retour si pas d'email en session
      }, 1000);
    }
  }, [navigate]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [step, setStep] = useState(1);
  const passwordInputRef = useRef(null);

  const [emailVerified, setEmailVerified] = useState(false);

  const getValidationSchema = (step) => {
    const baseSchema = {
      otp: Yup.string()
        .length(codeLength, `Le code doit contenir ${codeLength || 6} chiffres`)
        .matches(new RegExp(`^\\d{${codeLength || 6}}$`), "Code invalide")
        .required("Code requis"),
    };

    if (step === 2) {
      baseSchema.password = Yup.string()
        .min(6, "Minimum 6 caractères")
        .required("Mot de passe requis");
      baseSchema.confirmPassword = Yup.string()
        .oneOf([Yup.ref("password")], "Les mots de passe ne correspondent pas")
        .required("Confirmation requise");
    }

    return Yup.object(baseSchema);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      otp: "",

      password: "",
      confirmPassword: "",
    },

    validationSchema: getValidationSchema(step),
    onSubmit: async (values) => {
      setLoading(true);
      setEmailVerified(false);

      if (step === 1) {
        // Vérification du code OTP
        try {
          const data = {
            email: values.email,
            code: values.otp,
          };
          const response = await axiosToken.post(
            "/auth/verify-reset-code",
            data
          );
          if (response?.data?.success === true) {
            const resToken = response?.data?.resetToken || "";
            setAuthToken(resToken || "");
            toast.success(
              response?.data?.message || "Code vérifié avec succès."
            );

            setStep(2);
            setFocusOptInput(false);
            passwordInputRef.current?.focus();
            setLoading(false);
          } else {
            toast.error(
              response?.data?.message ||
              "Echec de vérification du code de réinitialisation."
            );
          }
        } catch (error) {
          console.error(
            "Echec de vérification du code de réinitialisation. Erreur: ",
            error
          );
          toast.error(
            error?.response?.data?.error ||
            "Echec de vérification du code de réinitialisation."
          );
          setLoading(false);
        }
        return;
      } else {
        // Soumission du nouveau mot de passe
        try {
          const data = {
            password: values.password,
            resetToken: authToken,
          };

          const response = await axiosToken.post("/auth/reset-password", data);

          toast.success("Réinitialisation réussie !", {
            position: "top-right",
          });

          sessionStorage.removeItem("forgot-email");
          setLoading(false);
          setEmailVerified(true);

          return response.data;
        } catch (error) {
          console.log("Message erreur: ", error?.response?.data?.error);
          toast.error(
            error?.response?.data?.error ||
            "Erreur lors de l'inscription. Veuillez réessayer.",
            { position: "bottom-left" }
          );
          console.error("Erreur lors de la récupération des services:", error);
          setLoading(false);
          return [];
        }
      }
    },
  });

  document.title = "Reinitialisation du mot de passe || Barberlys";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-xl bg-white shadow-xl p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          {/* <span className="text-sm bg-secondary-50 text-secondary-700 px-3 py-1 font-medium">
            Mot de passe oublié ?
          </span> */}
          <Link to={`/index`} className="cursor-pointer">
            <img src={logo200} className="m-auto" />
          </Link>
          <h2 className="text-2xl font-semibold mt-3">
            Réinitialisation de mot de passe
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.4 }}
              className="space-y-5"
            >
              <div className="mb-4">
                <p className="text-center text-gray-500">
                  Un e-mail de validation a été envoyé à votre adresse :
                  <span className="font-bold">
                    {formik.values.email
                      ? ` ${formik.values.email}`
                      : " (aucun email trouvé)"}
                  </span>
                </p>
              </div>

              <div className="space-y-1">
                <OTPInput
                  focus={focusOptInput}
                  setFocus={setFocusOptInput}
                  value={formik.values.otp}
                  onChange={formik.setFieldValue}
                />
                {formik.touched.otp && formik.errors.otp && (
                  <p className="text-red-400 text-sm text-center">
                    {formik.errors.otp}
                  </p>
                )}
              </div>

              {/* Button S'inscrire ou Valider */}
              <div className="flex justify-between items-center mb-6">
                {/* Button retour  */}
                <button
                  type="button"
                  onClick={() => {
                    navigate(-1);
                    setLoading(false);
                  }}
                  className="bg-black text-white py-2 px-4 shadow hover:bg-gray-500 font-semibold transition-all"
                >
                  Retour
                </button>

                <button
                  disabled={!(formik.isValid && formik.dirty) || loading}
                  type="submit"
                  className={`bg-secondary-300 ${!(formik.isValid && formik.dirty)
                    ? ""
                    : " hover:bg-secondary-400"
                    } text-white py-2 px-4 font-semibold transition-all`}
                >
                  {loading ? (
                    <div className="flex flex-row justify-center items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white" />
                      <span>Vérification...</span>
                    </div>
                  ) : (
                    "Continuer"
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <>
              {!emailVerified ? (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="space-y-5">
                    <InputField
                      ref={passwordInputRef}
                      formik={formik}
                      type="password"
                      name="password"
                      placeholder="Mot de passe"
                      className="w-full placeholder-gray-400 px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />


                    <InputField
                      formik={formik}
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirmer le mot de passe"
                      className="w-full placeholder-gray-400 px-4 py-2 shadow focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />


                    {/* Button S'inscrire ou Valider */}
                    <div className="w-full flex justify-between items-center mb-6">
                      {/* Button retour  */}
                      <button
                        type="button"
                        onClick={() => {
                          formik.setFieldValue("password", "");
                          formik.setFieldValue("confirmPassword", "");

                          setStep(1);
                          setFocusOptInput(true);
                        }}
                        className="bg-white/20 hover:bg-white/20 text-white py-2 px-4 font-semibold transition-all"
                      >
                        Retour
                      </button>

                      <button
                        disabled={!(formik.isValid && formik.dirty) || loading}
                        type="submit"
                        className={`bg-secondary-300 ${!(formik.isValid && formik.dirty)
                          ? ""
                          : " hover:bg-secondary-400"
                          } text-white py-2 px-4 font-semibold transition-all`}
                      >
                        {loading ? (
                          <div className="flex flex-row justify-center items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white" />
                            <span>Réinitialisation...</span>
                          </div>
                        ) : (
                          "Réinitialiser"
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="verified"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="text-center space-y-6">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1.2 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 10,
                      }}
                      className="flex justify-center"
                    >
                      <MdVerified
                        size={64}
                        className="text-green-400 drop-shadow-lg"
                      />
                    </motion.div>

                    <h3 className="text-xl font-semibold">
                      Mot de passe réinitialiser !
                    </h3>
                    <p className="text-gray-300">
                      Vous pouvez maintenant vous connecter à nouveau.
                    </p>
                    <button
                      onClick={() => navigate(`/${callback || "login"}`)}
                      className="bg-secondary-300 hover:bg-secondary-400 text-white py-2 px-4 font-semibold transition-all"
                    >
                      Continuer
                    </button>
                  </div>
                </motion.div>
              )}
            </>
          )}

          {/* Lien vers la page de connexion */}

          <p className="text-center text-sm mt-4">
            <Link
              to="/login"
              className="text-secondary-500 hover:text-secondary-700"
            >
              Je connais mon mot de passe {/* se connecter */}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
